import { useEffect, useState, useCallback, useMemo } from 'react';
import { useOrganization } from '../contexts/OrganizationContext';
import { useAuth } from '../contexts/AuthContext';
import {
  getFolders, createFolder, deleteFolder,
  getDocuments, uploadDocument, deleteDocument, downloadDocument,
} from '../services/ApiService';
import type { Folder, Document } from '../types/models';
import Modal from '../components/Modal';
import { FolderOpen, FileDown, Trash2, Upload, ArrowLeft, FolderPlus, ChevronRight } from 'lucide-react';
import { AnalyticsEvents, track } from '../services/AnalyticsService';

export default function DocumentsPage() {
  const { currentOrg, isBoardMember, updateLastSeen } = useOrganization();
  const { user } = useAuth();
  const [folders, setFolders] = useState<Folder[]>([]);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [currentFolder, setCurrentFolder] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [folderName, setFolderName] = useState('');
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    if (!currentOrg) return;
    try {
      const [f, d] = await Promise.all([
        getFolders(currentOrg.id),
        currentFolder ? getDocuments(currentOrg.id, currentFolder) : Promise.resolve([]),
      ]);
      setFolders(f);
      setDocuments(d);
    } catch {
      setFolders([]);
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  }, [currentOrg, currentFolder]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { if (currentOrg) updateLastSeen('documents'); }, [currentOrg, updateLastSeen]);

  const breadcrumbs = useMemo(() => {
    if (!currentFolder) return [];
    const foldersById = new Map(folders.map(f => [f.id, f]));
    const trail: Folder[] = [];
    let folderId: string | null = currentFolder;
    while (folderId) {
      const folder = foldersById.get(folderId);
      if (!folder) break;
      trail.unshift(folder);
      folderId = folder.parentFolderId;
    }
    return trail;
  }, [currentFolder, folders]);

  const childFolders = useMemo(() =>
    folders.filter(f => f.parentFolderId === currentFolder),
  [folders, currentFolder]);

  const handleCreateFolder = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentOrg || !user) return;
    setSaving(true);
    try {
      await createFolder(currentOrg.id, { name: folderName, parentFolderId: currentFolder });
      setShowFolderModal(false);
      setFolderName('');
      fetchData();
    } catch {
      alert('Kunde inte skapa mappen.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteFolder = async (id: string) => {
    if (!currentOrg) return;
    if (!window.confirm('Ta bort denna mapp och alla undermappar? Dokumenten i mapparna tas även bort.')) return;
    try {
      await deleteFolder(currentOrg.id, id);
      if (currentFolder === id) {
        const parent = folders.find(f => f.id === id)?.parentFolderId ?? null;
        setCurrentFolder(parent);
      }
      fetchData();
    } catch {
      alert('Kunde inte ta bort mappen.');
    }
  };

  const handleUploadDocument = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentOrg || !user || !uploadFile) return;
    setSaving(true);
    try {
      if (!currentFolder) {
        alert('Välj en mapp innan du laddar upp ett dokument.');
        setSaving(false);
        return;
      }
      await uploadDocument(currentOrg.id, currentFolder, uploadFile);
      track(AnalyticsEvents.DocumentUploaded, { orgId: currentOrg.id, fileType: uploadFile.type || null, fileSize: uploadFile.size });
      setShowUploadModal(false);
      setUploadFile(null);
      fetchData();
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Fel vid uppladdning.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteDocument = async (doc: Document) => {
    if (!currentOrg) return;
    if (!window.confirm(`Ta bort "${doc.name}"?`)) return;
    try {
      await deleteDocument(currentOrg.id, doc.id);
      fetchData();
    } catch {
      alert('Kunde inte ta bort dokumentet.');
    }
  };

  const handleDownloadDocument = async (doc: Document) => {
    if (!currentOrg) return;
    try {
      await downloadDocument(currentOrg.id, doc.id, doc.name);
    } catch {
      // open direct URL as fallback
      window.open(doc.downloadUrl, '_blank');
    }
  };

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (d: string) => new Date(d).toLocaleDateString('sv-SE');

  if (loading) return <div className="p-8 text-center text-gray-500">Laddar...</div>;

  return (
    <div className="max-w-4xl mx-auto p-6 lg:p-8">
      <div className="flex items-center justify-between mb-6">
        <div className="min-w-0 flex-1">
          {currentFolder && (
            <div className="flex items-center gap-1 mb-1.5 flex-wrap">
              <button
                onClick={() => setCurrentFolder(null)}
                className="text-teal-700 hover:text-teal-800 flex items-center gap-1 text-sm font-medium shrink-0"
              >
                <ArrowLeft className="w-4 h-4" />
                Dokument
              </button>
              {breadcrumbs.slice(0, -1).map(bc => (
                <span key={bc.id} className="flex items-center gap-1 shrink-0">
                  <ChevronRight className="w-3.5 h-3.5 text-gray-400" />
                  <button
                    onClick={() => setCurrentFolder(bc.id)}
                    className="text-teal-700 hover:text-teal-800 text-sm font-medium"
                  >
                    {bc.name}
                  </button>
                </span>
              ))}
              {breadcrumbs.length > 0 && (
                <span className="flex items-center gap-1 shrink-0">
                  <ChevronRight className="w-3.5 h-3.5 text-gray-400" />
                  <span className="text-sm text-gray-500">{breadcrumbs[breadcrumbs.length - 1].name}</span>
                </span>
              )}
            </div>
          )}
          <h1 className="text-2xl font-bold text-gray-900">
            {currentFolder ? breadcrumbs[breadcrumbs.length - 1]?.name ?? 'Dokument' : 'Dokument'}
          </h1>
          <p className="text-gray-500 text-sm mt-1">Föreningens gemensamma filer</p>
        </div>
        {isBoardMember && (
          <div className="flex gap-2 shrink-0">
            <button
              data-tour="folder-add"
              onClick={() => setShowFolderModal(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 rounded-xl font-medium text-sm"
            >
              <FolderPlus className="w-4 h-4" />
              <span className="hidden sm:inline">Ny mapp</span>
            </button>
            <button
              data-tour={currentFolder ? 'document-upload' : undefined}
              onClick={() => setShowUploadModal(true)}
              disabled={!currentFolder}
              title={!currentFolder ? 'Öppna en mapp för att ladda upp' : undefined}
              className="flex items-center gap-2 px-4 py-2.5 bg-teal-700 hover:bg-teal-800 text-white rounded-xl font-medium text-sm disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Upload className="w-4 h-4" />
              <span className="hidden sm:inline">Ladda upp</span>
            </button>
          </div>
        )}
      </div>

      {childFolders.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-6">
          {childFolders.map(f => (
            <div key={f.id} className="relative group">
              <button
                data-tour="folder-open"
                onClick={() => setCurrentFolder(f.id)}
                className="w-full bg-white rounded-xl border border-gray-200 p-4 text-left hover:border-teal-300 hover:shadow-sm transition-all"
              >
                <FolderOpen className="w-8 h-8 text-teal-600 mb-2" />
                <p className="font-medium text-gray-900 text-sm truncate">{f.name}</p>
                <p className="text-xs text-gray-400 mt-0.5">
                  {f.documentCount} {f.documentCount === 1 ? 'dokument' : 'dokument'}
                </p>
              </button>
              {isBoardMember && (
                <button
                  onClick={() => handleDeleteFolder(f.id)}
                  className="absolute top-2 right-2 p-1 rounded-lg bg-white shadow-sm opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-50"
                >
                  <Trash2 className="w-3.5 h-3.5 text-gray-400" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {documents.length === 0 && childFolders.length === 0 ? (
        <div className="text-center py-12">
          <FolderOpen className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">{currentFolder ? 'Tomt i denna mapp' : 'Inga dokument uppladdade'}</p>
          {isBoardMember && (
            <p className="text-gray-400 text-sm mt-1">
              {currentFolder ? 'Ladda upp filer eller skapa undermappar' : 'Skapa en mapp eller ladda upp filer'}
            </p>
          )}
        </div>
      ) : documents.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="divide-y divide-gray-100">
            {documents.map(doc => (
              <div key={doc.id} className="px-5 py-3.5 flex items-center justify-between hover:bg-gray-50 transition-colors">
                <div className="flex-1 min-w-0 mr-4">
                  <p className="font-medium text-gray-900 text-sm truncate">{doc.name}</p>
                  <div className="flex items-center gap-2 text-xs text-gray-400 mt-0.5">
                    <span>{formatSize(doc.fileSize)}</span>
                    <span>|</span>
                    <span>{formatDate(doc.createdAt)}</span>
                    <span>|</span>
                    <span>{doc.uploadedBy}</span>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => handleDownloadDocument(doc)} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
                    <FileDown className="w-4 h-4 text-gray-500" />
                  </button>
                  {isBoardMember && (
                    <button onClick={() => handleDeleteDocument(doc)} className="p-2 rounded-lg hover:bg-red-50 transition-colors">
                      <Trash2 className="w-4 h-4 text-gray-400" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <Modal isOpen={showFolderModal} onClose={() => setShowFolderModal(false)} title={currentFolder ? 'Ny undermapp' : 'Ny mapp'} size="sm">
        <form onSubmit={handleCreateFolder} className="space-y-4">
          {currentFolder && breadcrumbs.length > 0 && (
            <p className="text-xs text-gray-400">
              Skapas i: {breadcrumbs.map(b => b.name).join(' / ')}
            </p>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Mappnamn</label>
            <input type="text" value={folderName} onChange={e => setFolderName(e.target.value)} required className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none" />
          </div>
          <div className="flex gap-3 justify-end">
            <button type="button" onClick={() => setShowFolderModal(false)} className="px-4 py-2 text-gray-600">Avbryt</button>
            <button type="submit" disabled={saving} className="px-6 py-2 bg-teal-700 hover:bg-teal-800 text-white rounded-xl font-medium disabled:opacity-50">
              {saving ? 'Skapar...' : 'Skapa'}
            </button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={showUploadModal} onClose={() => setShowUploadModal(false)} title="Ladda upp dokument" size="sm">
        <form onSubmit={handleUploadDocument} className="space-y-4">
          {currentFolder && breadcrumbs.length > 0 && (
            <p className="text-xs text-gray-400">
              Laddas upp i: {breadcrumbs.map(b => b.name).join(' / ')}
            </p>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Välj fil</label>
            <input
              type="file"
              onChange={e => setUploadFile(e.target.files?.[0] ?? null)}
              required
              className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-medium file:bg-teal-50 file:text-teal-700 hover:file:bg-teal-100"
            />
          </div>
          {uploadFile && (
            <p className="text-xs text-gray-500">{uploadFile.name} ({formatSize(uploadFile.size)})</p>
          )}
          <div className="flex gap-3 justify-end">
            <button type="button" onClick={() => setShowUploadModal(false)} className="px-4 py-2 text-gray-600">Avbryt</button>
            <button type="submit" disabled={saving || !uploadFile} className="px-6 py-2 bg-teal-700 hover:bg-teal-800 text-white rounded-xl font-medium disabled:opacity-50">
              {saving ? 'Laddar upp...' : 'Ladda upp'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
